/* global cornerstone */
import './ImageThumbnail.styl';

import { utils } from '@ohif/core';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import PropTypes from 'prop-types';
import ViewportErrorIndicator from '../../viewer/ViewportErrorIndicator';
import ViewportLoadingIndicator from '../../viewer/ViewportLoadingIndicator';

// TODO: How should we have this component depend on Cornerstone?
// - Passed in as a prop?
// - Set as external dependency?
// - Pass in the entire load and render function as a prop?
//import cornerstone from 'cornerstone-core';

function readXhrBody(xhr) {
  const response = xhr && xhr.response;
  if (!response) {
    return '';
  }
  if (typeof response === 'string') {
    return response.slice(0, 300);
  }
  if (typeof ArrayBuffer !== 'undefined' && response instanceof ArrayBuffer) {
    const bytes = new Uint8Array(response, 0, Math.min(response.byteLength, 300));
    let text = '';
    for (let i = 0; i < bytes.length; i++) {
      const code = bytes[i];
      if (code < 9 || (code > 13 && code < 32)) {
        return '';
      }
      text += String.fromCharCode(code);
    }
    return text;
  }
  return '';
}

function describeImageLoadError(error) {
  if (!error) {
    return 'unknown error';
  }

  const inner = error.error || error;
  if (inner && typeof inner.status === 'number') {
    const body = readXhrBody(inner);
    return `HTTP ${inner.status} ${inner.statusText || ''} ${body}`.trim();
  }
  if (inner instanceof Error) {
    return inner.message;
  }
  if (typeof inner === 'string') {
    return inner;
  }
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(inner);
  } catch (e) {
    return String(inner);
  }
}

function ImageThumbnail(props) {
  const {
    active,
    width,
    height,
    imageSrc,
    imageId,
    stackPercentComplete,
    error: propsError,
    showProgressBar,
  } = props;

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [image, setImage] = useState({});
  const canvasRef = useRef(null);

  let loadingOrError;

  if (propsError || error) {
    loadingOrError = <ViewportErrorIndicator />;
  } else if (isLoading) {
    loadingOrError = <ViewportLoadingIndicator />;
  }

  const showStackLoadingProgressBar =
    showProgressBar && stackPercentComplete !== undefined;

  const shouldRenderToCanvas = () => {
    return imageId && !imageSrc;
  };

  // imageId 不变时不要重新加载。进度条 setState 会让这个组件反复渲染，
  // 如果把每次渲染新建的函数放进依赖，会取消上一次请求并立刻再发一次。
  useEffect(() => {
    if (!imageId || imageSrc) {
      return undefined;
    }

    let canceled = false;
    let cancelablePromise;

    setLoading(true);
    setError(false);

    try {
      cancelablePromise = utils.makeCancelable(
        cornerstone.loadAndCacheImage(imageId)
      );
    } catch (loadError) {
      console.error(
        '缩略图加载失败',
        imageId,
        describeImageLoadError(loadError)
      );
      setLoading(false);
      setError(true);
      return undefined;
    }

    cancelablePromise
      .then(response => {
        if (canceled) {
          return;
        }
        setImage(response);
      })
      .catch(loadError => {
        if (canceled || (loadError && loadError.isCanceled)) {
          return;
        }
        console.error(
          '缩略图加载失败',
          imageId,
          describeImageLoadError(loadError)
        );
        setLoading(false);
        setError(true);
      });

    return () => {
      canceled = true;
      if (cancelablePromise) {
        cancelablePromise.cancel();
      }
    };
  }, [imageId, imageSrc]);

  useEffect(() => {
    if (image.imageId && canvasRef.current) {
      cornerstone.renderToCanvas(canvasRef.current, image);
      setLoading(false);
    }
  }, [image]);

  return (
    <div className={classNames('ImageThumbnail', { active: active })}>
      <div className="image-thumbnail-canvas">
        {shouldRenderToCanvas() ? (
          <canvas ref={canvasRef} width={width} height={height} />
        ) : (
          <img
            className="static-image"
            src={imageSrc}
            //width={this.props.width}
            height={height}
            alt={''}
          />
        )}
      </div>
      {loadingOrError}
      {showStackLoadingProgressBar && (
        <div className="image-thumbnail-progress-bar">
          <div
            className="image-thumbnail-progress-bar-inner"
            style={{ width: `${stackPercentComplete}%` }}
          />
        </div>
      )}
      {isLoading && <div className="image-thumbnail-loading-indicator"></div>}
    </div>
  );
}

ImageThumbnail.propTypes = {
  active: PropTypes.bool,
  imageSrc: PropTypes.string,
  imageId: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  stackPercentComplete: PropTypes.number.isRequired,
  showProgressBar: PropTypes.bool,
};

ImageThumbnail.defaultProps = {
  active: false,
  error: false,
  stackPercentComplete: 0,
  width: 217,
  height: 123,
  showProgressBar: true,
};

export default ImageThumbnail;
